/**
 * Bar Chart — vertical or horizontal, supports stacked and grouped variants.
 *
 * Usage:
 *   <BarChart
 *     data={[{label: "AP", value: 42}, {label: "AR", value: 28}]}
 *     height={300}
 *     unit="COUNT"
 *     horizontal
 *   />
 *
 * Multi-series:
 *   <BarChart
 *     data={[{label: "Q1", resolved: 80, pending: 20}]}
 *     series={[{key: "resolved", label: "Resolved"}, {key: "pending", label: "Pending"}]}
 *     stacked
 *   />
 */

import React from 'react';
import {
  ResponsiveContainer,
  BarChart as RechartsBarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { CHART_COLORS, RECHARTS_DEFAULTS, formatMetricValue, CHART_FONTS } from '../../config/chartTheme';

interface BarSeries {
  key: string;
  label: string;
  color?: string;
}

interface BarChartProps {
  data: Record<string, any>[];
  series?: BarSeries[];
  height?: number;
  unit?: string;
  horizontal?: boolean;
  stacked?: boolean;
  showLegend?: boolean;
  onClick?: (entry: any) => void;
  labelKey?: string;
  valueKey?: string;
  color?: string;
  barSize?: number;
}

const CustomTooltip = ({ active, payload, label, unit }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={RECHARTS_DEFAULTS.tooltipContentStyle}>
      <div style={RECHARTS_DEFAULTS.tooltipLabelStyle}>{label}</div>
      {payload.map((entry: any, i: number) => (
        <div key={i} style={{ color: entry.color, fontSize: '12px', marginTop: i > 0 ? '2px' : 0 }}>
          {entry.name}: {formatMetricValue(entry.value, unit || 'COUNT')}
        </div>
      ))}
    </div>
  );
};

export const BarChart: React.FC<BarChartProps> = ({
  data, series, height = 300, unit = 'COUNT',
  horizontal = false, stacked = false, showLegend = false,
  onClick, labelKey = 'label', valueKey = 'value',
  color = CHART_COLORS.accent, barSize,
}) => {
  const handleClick = onClick
    ? (entry: any) => { if (entry?.activePayload?.[0]?.payload) onClick(entry.activePayload[0].payload); }
    : undefined;

  // Single-series or multi-series
  const bars = series
    ? series.map((s, i) => (
        <Bar
          key={s.key}
          dataKey={s.key}
          name={s.label}
          fill={s.color || CHART_COLORS.series[i % CHART_COLORS.series.length]}
          stackId={stacked ? 'stack' : undefined}
          radius={stacked ? undefined : [4, 4, 0, 0]}
          barSize={barSize}
        />
      ))
    : [
        <Bar
          key={valueKey}
          dataKey={valueKey}
          fill={color}
          radius={[4, 4, 0, 0]}
          barSize={barSize}
        />,
      ];

  const layout = horizontal ? 'vertical' : 'horizontal';

  const categoryAxis = horizontal
    ? <YAxis type="category" dataKey={labelKey} {...RECHARTS_DEFAULTS.xAxisProps} width={120} />
    : <XAxis dataKey={labelKey} {...RECHARTS_DEFAULTS.xAxisProps} />;

  const valueAxis = horizontal
    ? <XAxis type="number" {...RECHARTS_DEFAULTS.yAxisProps} tickFormatter={(v: number) => formatMetricValue(v, unit)} />
    : <YAxis {...RECHARTS_DEFAULTS.yAxisProps} tickFormatter={(v: number) => formatMetricValue(v, unit)} />;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart data={data} layout={layout} onClick={handleClick}>
        <CartesianGrid {...RECHARTS_DEFAULTS.gridProps} />
        {categoryAxis}
        {valueAxis}
        <Tooltip content={<CustomTooltip unit={unit} />} cursor={RECHARTS_DEFAULTS.tooltipCursor} />
        {showLegend && (
          <Legend
            wrapperStyle={{
              fontFamily: CHART_FONTS.sans,
              fontSize: '11px',
              color: CHART_COLORS.textSecondary,
            }}
          />
        )}
        {bars}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

export default BarChart;
